import {useEffect, useState} from "react";
import {
    Button, Card, Input, message, Modal, Popconfirm, Progress, Space, Table, Tag, Tooltip, Typography, Upload
} from "antd";
import {
    CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, ReloadOutlined, UploadOutlined
} from "@ant-design/icons";
import ApiService from "../../../services/ApiService";

const LAYER_ACCEPT = '.kml,.kmz,.geojson,.json,.zip,.fgb';
const PROJ_ACCEPT = '.prj,.txt,.wkt';

function formatBytes(n) {
    if (!n && n !== 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let v = n, i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatDate(epochSeconds) {
    if (!epochSeconds) return '';
    return new Date(epochSeconds * 1000).toLocaleString();
}

// Mirror of the backend slug rule (_SLUG_RE): only [A-Za-z0-9_-] survive.
function slugifyInput(s) {
    return (s || '').replace(/[^A-Za-z0-9_-]+/g, '_');
}

// Inline text editor. Click pencil to edit, ✓ to save, ✗ to cancel.
// Pass `sanitize` to constrain input (e.g. slugs) and `code` to render the
// value monospaced. onSave should resolve truthy on success.
function InlineEditor({value, onSave, sanitize, code, width = 200}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const [saving, setSaving] = useState(false);

    useEffect(() => { setDraft(value || ''); }, [value]);

    const commit = async () => {
        const next = sanitize ? sanitize(draft) : draft.trim();
        if (!next || next === value) { setEditing(false); setDraft(value || ''); return; }
        setSaving(true);
        try {
            const ok = await onSave(next);
            if (ok) setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <Space size={4}>
                {code ? <Typography.Text code>{value}</Typography.Text> : <span>{value}</span>}
                <Button
                    type={'text'}
                    size={'small'}
                    icon={<EditOutlined/>}
                    onClick={() => setEditing(true)}
                />
            </Space>
        );
    }

    return (
        <Space.Compact>
            <Input
                size={'small'}
                value={draft}
                autoFocus
                onChange={(e) => setDraft(sanitize ? sanitize(e.target.value) : e.target.value)}
                onPressEnter={commit}
                style={{width}}
                disabled={saving}
            />
            <Button size={'small'} icon={<CheckOutlined/>} loading={saving} onClick={commit}/>
            <Button size={'small'} icon={<CloseOutlined/>} disabled={saving}
                    onClick={() => { setEditing(false); setDraft(value || ''); }}/>
        </Space.Compact>
    );
}

// Multi-file upload modal: lets the user pick multiple files, tweak each
// display name, and uploads them sequentially with per-file progress.
function UploadModal({open, title, accept, onClose, onUpload}) {
    const [files, setFiles] = useState([]); // [{file, displayName, status, percent, processing, error}]
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) { setFiles([]); }
    }, [open]);

    const addFile = (f) => {
        setFiles(prev => [
            ...prev,
            {
                file: f,
                displayName: f.name.replace(/\.[^.]+$/, ''),
                status: 'pending',
                percent: 0,
                processing: false,
                error: null,
            },
        ]);
    };

    const updateRow = (idx, patch) => {
        setFiles(prev => {
            const next = [...prev];
            next[idx] = {...next[idx], ...patch};
            return next;
        });
    };

    const removeRow = (idx) => {
        if (busy) return;
        setFiles(prev => prev.filter((_, i) => i !== idx));
    };

    const submit = async () => {
        if (!files.length) return;
        setBusy(true);
        let allOk = true;
        for (let i = 0; i < files.length; i++) {
            const row = files[i];
            if (row.status === 'done') continue;
            updateRow(i, {status: 'uploading', percent: 0, processing: false, error: null});
            const res = await onUpload(
                row.file,
                row.displayName || row.file.name.replace(/\.[^.]+$/, ''),
                (p) => updateRow(i, {percent: p.percent, processing: !!p.processing}),
            );
            if (res?.success) {
                updateRow(i, {status: 'done', percent: 100, processing: false});
            } else {
                allOk = false;
                updateRow(i, {status: 'error', error: res?.data?.error || res?.data?.Message || 'Failed'});
            }
        }
        setBusy(false);
        if (allOk) onClose(true);
    };

    const allDone = files.length > 0 && files.every(f => f.status === 'done');

    return (
        <Modal
            title={title}
            open={open}
            onCancel={() => onClose(allDone)}
            confirmLoading={busy}
            okText={busy ? 'Uploading...' : (allDone ? 'Close' : `Upload ${files.length || ''}`)}
            okButtonProps={{disabled: !files.length}}
            cancelButtonProps={{disabled: busy}}
            maskClosable={!busy}
            closable={!busy}
            onOk={allDone ? () => onClose(true) : submit}
            width={720}
            destroyOnClose
        >
            <Space direction={'vertical'} style={{width: '100%'}} size={'middle'}>
                <Upload
                    beforeUpload={(file) => { addFile(file); return false; }}
                    multiple
                    showUploadList={false}
                    accept={accept}
                    disabled={busy}
                >
                    <Button icon={<UploadOutlined/>} disabled={busy}>
                        Select files ({accept})
                    </Button>
                </Upload>
                {files.length > 0 && (
                    <div style={{maxHeight: 380, overflowY: 'auto'}}>
                        {files.map((row, idx) => (
                            <div key={idx} style={{
                                display: 'flex', flexDirection: 'column', gap: 4,
                                padding: '8px 0', borderBottom: '1px solid #f0f0f0',
                            }}>
                                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                    <Input
                                        size={'small'}
                                        value={row.displayName}
                                        placeholder={'Display name'}
                                        onChange={(e) => updateRow(idx, {displayName: e.target.value})}
                                        disabled={busy || row.status === 'done'}
                                        style={{flex: 1}}
                                    />
                                    <Typography.Text type={'secondary'} style={{fontSize: 11, minWidth: 70, textAlign: 'right'}}>
                                        {formatBytes(row.file.size)}
                                    </Typography.Text>
                                    <Button
                                        size={'small'}
                                        type={'text'}
                                        icon={<CloseOutlined/>}
                                        disabled={busy}
                                        onClick={() => removeRow(idx)}
                                    />
                                </div>
                                <Typography.Text type={'secondary'} style={{fontSize: 11}} ellipsis>
                                    {row.file.name}
                                </Typography.Text>
                                {(row.status === 'uploading' || row.status === 'done' || row.status === 'error') && (
                                    <Progress
                                        size={'small'}
                                        percent={Math.round(row.percent)}
                                        status={
                                            row.status === 'error' ? 'exception'
                                                : row.status === 'done' ? 'success'
                                                    : row.processing ? 'active' : 'normal'
                                        }
                                        strokeColor={row.processing && row.status !== 'done' ? '#faad14' : undefined}
                                        format={(p) => row.status === 'error'
                                            ? row.error
                                            : row.processing && row.status !== 'done'
                                                ? 'Converting...'
                                                : `${p}%`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Space>
        </Modal>
    );
}

function LayersSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const res = await ApiService.Layers.List();
            setItems(Array.isArray(res?.data) ? res.data : []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const handleDelete = async (name) => {
        const res = await ApiService.Layers.Delete(name);
        if (res?.success) refresh();
    };

    const handleRename = async (name, displayName) => {
        const res = await ApiService.Layers.Rename(name, displayName);
        if (res?.success) refresh();
        return !!res?.success;
    };

    const handleRenameSlug = async (name, slug) => {
        const res = await ApiService.Layers.Rename(name, null, slug);
        if (res?.success) { refresh(); return true; }
        message.error(res?.data?.error || 'Could not rename slug');
        return false;
    };

    const columns = [
        {
            title: 'Display name', dataIndex: 'display_name', key: 'display_name',
            render: (v, row) => (
                <InlineEditor value={v} onSave={(next) => handleRename(row.name, next)}/>
            ),
        },
        {
            title: 'Slug', dataIndex: 'name', key: 'name',
            render: (v, row) => (
                <InlineEditor value={v} code sanitize={slugifyInput} width={220}
                              onSave={(next) => handleRenameSlug(row.name, next)}/>
            ),
        },
        {title: 'Geometry', dataIndex: 'geometry_type', key: 'geometry_type', render: (v) => <Tag>{v}</Tag>},
        {title: 'Features', dataIndex: 'feature_count', key: 'feature_count', align: 'right'},
        {title: 'Size', dataIndex: 'size_bytes', key: 'size_bytes', align: 'right', render: formatBytes},
        {title: 'Source', dataIndex: 'source_format', key: 'source_format', render: (v) => <Tag color={'blue'}>{v}</Tag>},
        {title: 'Bounding box', dataIndex: 'bbox', key: 'bbox', render: (v) => v ? (
            <Tooltip title={v.map((x) => x.toFixed(4)).join(', ')}>
                <Typography.Text type={'secondary'}>{v.map((x) => x.toFixed(2)).join(', ')}</Typography.Text>
            </Tooltip>
        ) : ''},
        {title: 'Uploaded', dataIndex: 'uploaded_at', key: 'uploaded_at', render: formatDate},
        {title: 'By', dataIndex: 'uploaded_by', key: 'uploaded_by'},
        {title: '', key: 'actions', align: 'right', render: (_, row) => (
            <Popconfirm
                title={'Delete this item?'}
                description={row.display_name}
                okText={'Delete'}
                okType={'danger'}
                placement={'topRight'}
                overlayStyle={{maxWidth: 320}}
                onConfirm={() => handleDelete(row.name)}
            >
                <Button danger size={'small'} icon={<DeleteOutlined/>}/>
            </Popconfirm>
        )},
    ];

    return (
        <Card
            size={'small'}
            title={'Reference layers'}
            extra={
                <Space>
                    <Tooltip title={'Refresh'}>
                        <Button icon={<ReloadOutlined/>} loading={loading} onClick={refresh} size={'small'}/>
                    </Tooltip>
                    <Button type={'primary'} icon={<UploadOutlined/>} onClick={() => setModalOpen(true)} size={'small'}>
                        Upload layers
                    </Button>
                </Space>
            }
        >
            <Typography.Paragraph type={'secondary'} style={{marginTop: 0}}>
                Supported formats: KML, KMZ, GeoJSON, Shapefile (zip), FlatGeobuf.
                Uploads are converted to FlatGeobuf on the server for fast bounding-box queries.
            </Typography.Paragraph>
            <Table
                rowKey={'name'}
                dataSource={items}
                columns={columns}
                pagination={{pageSize: 20}}
                size={'small'}
                loading={loading}
            />
            <UploadModal
                open={modalOpen}
                title={'Upload reference layers'}
                accept={LAYER_ACCEPT}
                onClose={(uploaded) => { setModalOpen(false); if (uploaded) refresh(); }}
                onUpload={(file, name, onProgress) => ApiService.Layers.Upload(file, name, onProgress)}
            />
        </Card>
    );
}

function ProjectionsSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const res = await ApiService.Projections.List();
            setItems(Array.isArray(res?.data) ? res.data : []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const handleDelete = async (name) => {
        const res = await ApiService.Projections.Delete(name);
        if (res?.success) refresh();
    };

    const handleRename = async (name, displayName) => {
        const res = await ApiService.Projections.Rename(name, displayName);
        if (res?.success) refresh();
        return !!res?.success;
    };

    const handleRenameSlug = async (name, slug) => {
        const res = await ApiService.Projections.Rename(name, null, slug);
        if (res?.success) { refresh(); return true; }
        message.error(res?.data?.error || 'Could not rename slug');
        return false;
    };

    const columns = [
        {
            title: 'Display name', dataIndex: 'display_name', key: 'display_name',
            render: (v, row) => (
                <InlineEditor value={v} onSave={(next) => handleRename(row.name, next)}/>
            ),
        },
        {
            title: 'Slug', dataIndex: 'name', key: 'name',
            render: (v, row) => (
                <InlineEditor value={v} code sanitize={slugifyInput} width={220}
                              onSave={(next) => handleRenameSlug(row.name, next)}/>
            ),
        },
        {title: 'Size', dataIndex: 'size_bytes', key: 'size_bytes', align: 'right', render: formatBytes},
        {title: 'Uploaded', dataIndex: 'uploaded_at', key: 'uploaded_at', render: formatDate},
        {title: 'By', dataIndex: 'uploaded_by', key: 'uploaded_by'},
        {title: '', key: 'actions', align: 'right', render: (_, row) => (
            <Popconfirm
                title={'Delete this item?'}
                description={row.display_name}
                okText={'Delete'}
                okType={'danger'}
                placement={'topRight'}
                overlayStyle={{maxWidth: 320}}
                onConfirm={() => handleDelete(row.name)}
            >
                <Button danger size={'small'} icon={<DeleteOutlined/>}/>
            </Popconfirm>
        )},
    ];

    return (
        <Card
            size={'small'}
            title={'Projections (.prj)'}
            style={{marginTop: 16}}
            extra={
                <Space>
                    <Tooltip title={'Refresh'}>
                        <Button icon={<ReloadOutlined/>} loading={loading} onClick={refresh} size={'small'}/>
                    </Tooltip>
                    <Button type={'primary'} icon={<UploadOutlined/>} onClick={() => setModalOpen(true)} size={'small'}>
                        Upload projections
                    </Button>
                </Space>
            }
        >
            <Typography.Paragraph type={'secondary'} style={{marginTop: 0}}>
                Plain WKT projection files (.prj / .txt / .wkt). Used as projection options on the map view.
            </Typography.Paragraph>
            <Table
                rowKey={'name'}
                dataSource={items}
                columns={columns}
                pagination={{pageSize: 20}}
                size={'small'}
                loading={loading}
            />
            <UploadModal
                open={modalOpen}
                title={'Upload projections'}
                accept={PROJ_ACCEPT}
                onClose={(uploaded) => { setModalOpen(false); if (uploaded) refresh(); }}
                onUpload={(file, name, onProgress) => ApiService.Projections.Upload(file, name, onProgress)}
            />
        </Card>
    );
}

export default function MapAssetsTab() {
    return (
        <div>
            <LayersSection/>
            <ProjectionsSection/>
        </div>
    );
}
